import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const tools = [
  { slug: "real-paycheck-estimator", emoji: "💵", title: "Real Paycheck Estimator", desc: "See what actually lands in your bank account after taxes before you accept the job.", category: "Career", comingSoon: true },
  { slug: "50-30-20-budget", emoji: "📊", title: "50/30/20 Budget Calculator", desc: "Figure out in 30 seconds how much of your paycheck should go toward rent, fun, and savings every month.", category: "Budgeting", comingSoon: false },
  { slug: "cost-in-hours", emoji: "⏱️", title: "Cost in Hours Calculator", desc: "Type in a price and your hourly wage — see how many hours of your life that purchase actually costs.", category: "Budgeting", comingSoon: false },
  { slug: "w4-withholding-estimator", emoji: "📝", title: "W-4 Withholding Estimator", desc: "Set up your withholding with confidence so tax time holds fewer surprises.", category: "Career", comingSoon: false },
  { slug: "rent-affordability", emoji: "🏠", title: "Rent Affordability Calculator", desc: "Type in your income and see the max rent you can afford without stretching your budget dangerously thin.", category: "Budgeting", comingSoon: false },
  { slug: "student-loan-payoff", emoji: "🎓", title: "Student Loan Payoff Calculator", desc: "See exactly when you’ll be debt-free and how much interest your loan will cost.", category: "Debt", comingSoon: false },
  { slug: "emergency-fund-targeter", emoji: "🛡️", title: "Emergency Fund Targeter", desc: "Find out exactly how much cash you should have saved before a job loss or unexpected expense wrecks your finances.", category: "Saving", comingSoon: false },
  { slug: "compound-interest", emoji: "📈", title: "Compound Interest Calculator", desc: "See how money grows when you leave it alone — and why starting even $50/month in your 20s is a huge deal.", category: "Saving", comingSoon: false },
  { slug: "credit-utilization", emoji: "💳", title: "Credit Utilization Calculator", desc: "Enter your card balances and limits to check if your credit usage is in the healthy zone lenders want to see.", category: "Credit", comingSoon: false },
  { slug: "min-payment-trap", emoji: "⚠️", title: "Credit Card Minimum-Payment Trap Calculator", desc: "See what happens if you only pay the minimum on a credit card — the timeline and the true cost will shock you.", category: "Credit", comingSoon: false },
  { slug: "subscription-cost-tracker", emoji: "🔁", title: "Subscription Cost Tracker", desc: "Add up every streaming service, app, and gym membership to see what you're actually spending per year.", category: "Budgeting", comingSoon: false },
  { slug: "hysa-comparison", emoji: "🏦", title: "HYSA/APY Comparison Calculator", desc: "Compare two savings accounts side by side to see exactly how much more interest a high-yield account earns.", category: "Saving", comingSoon: false },
  { slug: "net-worth-tracker", emoji: "📉", title: "Net Worth Tracker", desc: "List everything you own and everything you owe — your net worth is the single most honest number about your finances.", category: "Saving", comingSoon: false },
  { slug: "401k-match", emoji: "🤝", title: "401(k) Employer Match Calculator", desc: "Find out how much free money your employer is offering in 401(k) matching — and how much you'd leave behind by not contributing.", category: "Investing", comingSoon: false },
  { slug: "roth-vs-traditional", emoji: "🧾", title: "Roth vs. Traditional IRA Calculator", desc: "Compare a Roth IRA vs a Traditional IRA based on your actual income to see which one puts more money in your pocket at retirement.", category: "Investing", comingSoon: false },
  { slug: "retirement-number", emoji: "🌅", title: "Retirement Number Calculator", desc: "Use the 4% rule to calculate the exact savings target you need to retire on your own terms.", category: "Investing", comingSoon: false },
  { slug: "car-affordability", emoji: "🚗", title: "Car Affordability & Auto Loan Calculator", desc: "Find a car price that fits your budget before a dealership can stretch it for you.", category: "Debt", comingSoon: false },
];

export default function OtherTools() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [entered, setEntered] = useState(false);
  const categories = ["All", "Budgeting", "Saving", "Debt", "Credit", "Investing", "Career"];

  useEffect(() => {
    const timer = setTimeout(() => setEntered(true), 40);
    return () => clearTimeout(timer);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tools.filter((t) => {
      if (category !== "All" && t.category !== category) return false;
      if (!q) return true;
      return (
        t.title.toLowerCase().includes(q) ||
        t.desc.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q)
      );
    });
  }, [query, category]);
  return (
    <main style={{ padding: "120px 0 80px" }}>
      <div className="container" style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 40, marginBottom: 10 }}>Other Tools</h1>
            <p style={{ color: "var(--t2)", maxWidth: 720, margin: 0 }}>
              A growing toolkit of calculators built to help you make real financial decisions — from your first paycheck to your first 401(k) match. Pick a tool below to get started.
            </p>
          </div>
        </div>
      </div>

      <div className="container" style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <label htmlFor="tools-search" style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <input
              id="tools-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools or categories…"
              aria-label="Search tools"
              className="input"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", color: "var(--t1)" }}
            />
          </label>
          <div style={{ color: "var(--t3)", fontSize: 13 }}>{filtered.length} tools</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          {categories.map((item) => (
            <button key={item} onClick={() => setCategory(item)} className="font-mono" style={{ border: "1px solid var(--line)", borderRadius: 999, padding: "7px 11px", fontSize: 11, cursor: "pointer", background: category === item ? "var(--emerald)" : "var(--bg-elev)", color: category === item ? "white" : "var(--t2)" }}>{item}</button>
          ))}
        </div>
      </div>

      <div className="container">
        <div
          style={{
            display: "grid",
            gap: 18,
            gridTemplateColumns: "repeat(2, 1fr)",
          }}
          className="tools-grid"
        >
          {filtered.map((t, i) => (
            <Link
              key={t.slug}
              to={`/tools/${t.slug}`}
              aria-label={t.title}
              className="card-base tool-card"
              style={{
                padding: 20,
                borderRadius: 16,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                textDecoration: "none",
                color: "var(--t1)",
                transform: "translateY(0)",
                opacity: entered ? 1 : 0,
                transform: entered ? "translateY(0)" : "translateY(16px)",
                transition: `opacity 320ms ease ${i * 60}ms, transform 320ms ease ${i * 60}ms, box-shadow 200ms ease`,
              }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div className="font-mono" style={{ fontSize: 10, color: "var(--emerald)", marginBottom: 6, textTransform: "uppercase" }}>{t.category}</div>
                  <div aria-hidden style={{ width: 44, height: 44, borderRadius: 10, display: "grid", placeItems: "center", background: "var(--emerald-soft)", color: "var(--emerald)", fontSize: 20, flexShrink: 0 }}><span role="img" aria-label="icon">{t.emoji}</span></div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  {t.comingSoon && (<div style={{ display: "inline-block", background: "var(--bg-warm)", color: "var(--rust)", padding: "6px 8px", borderRadius: 8, fontSize: 12 }}>Coming Soon</div>)}
                </div>
              </div>

              <div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, marginBottom: 6 }}>{t.title}</div>
                <div style={{ color: "var(--t2)", fontSize: 14 }}>{t.desc}</div>
              </div>
              <div className="tool-card-action" aria-hidden>Try the calculator <span>→</span></div>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        .tools-grid { grid-template-columns: repeat(3, 1fr) !important; }
        @media (max-width: 768px) { .tools-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .tools-grid { grid-template-columns: 1fr !important; } }
        .tool-card { position: relative; overflow: hidden; }
        .tool-card::after { content: ''; position: absolute; inset: auto -35% -70% auto; width: 160px; height: 160px; border-radius: 50%; background: var(--emerald-soft); opacity: 0; transform: scale(.4); transition: 300ms var(--ease); pointer-events: none; }
        .tool-card:hover { transform: translateY(-4px) !important; box-shadow: var(--shadow-lg); border-color: var(--line-strong) !important; }
        .tool-card:hover::after { opacity: 1; transform: scale(1); }
        .tool-card > * { position: relative; z-index: 1; }
        .tool-card-action { margin-top: auto; color: var(--emerald); font: 600 11px 'JetBrains Mono', monospace; letter-spacing: .04em; opacity: .6; transform: translateX(-4px); transition: 180ms var(--ease); }
        .tool-card-action span { display: inline-block; font-size: 17px; line-height: 0; margin-left: 4px; transition: transform 180ms var(--ease); }
        .tool-card:hover .tool-card-action { opacity: 1; transform: translateX(0); }
        .tool-card:hover .tool-card-action span { transform: translateX(4px); }
        .tool-card:focus { outline: 3px solid rgba(16, 185, 129, 0.12); outline-offset: 4px; }
      `}</style>
    </main>
  );
}
